'use client'

import { type IMeta, type INotification, type IParsedNotificationData } from '@repo/sdk'
import React, {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useNotificationsApi } from '../api'

interface INotificationContext {
  addNotification: (notification: IParsedNotificationData | undefined) => void
  fetch: () => void
  metadata?: IMeta
  notifications: INotification[]
}

export const NotificationContext = createContext<INotificationContext | undefined>({
  addNotification: () => null,
  fetch: () => null,
  metadata: undefined,
  notifications: [],
})

export const NotificationContextProvider = ({
  accessToken,
  children,
}: {
  accessToken?: string
  children: ReactNode
}) => {
  const { getNotifications } = useNotificationsApi()
  const [notifications, setNotifications] = useState<INotification[]>([])
  const [metadata, setMetadata] = useState<IMeta>()

  const addNotification: INotificationContext['addNotification'] = notification => {
    if (!notification) return
    setNotifications(prev => [notification, ...prev])
  }

  const fetch = async () => {
    const response = await getNotifications()
    setNotifications(response?.data || [])
    setMetadata(response?.meta)
  }

  useEffect(() => {
    if (accessToken) fetch()
  }, [accessToken])

  const value = useMemo(
    () =>
      ({
        addNotification,
        fetch,
        metadata,
        notifications,
      }) satisfies INotificationContext,
    [notifications, metadata],
  )

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}

export const useNotifications = () => {
  const context = useContext(NotificationContext)

  if (!context) {
    throw new Error('useNotifications must be used within a AuthContextProvider')
  }

  return context
}
